import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/da2lhjbd3r93mn1k3bi0";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check — bot trap, keep silent on hit
    const trap = (formData.get("website_alt") as string)?.trim() ?? "";
    if (trap) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("loading");
    setFormError("");

    // Remove the empty honeypot field before submitting
    formData.delete("website_alt");

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = responseText ? JSON.parse(responseText) : {};
      } catch {
        parsed = {};
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;

      const looksSpam =
        !res.ok ||
        (parsed?.code && parsed.code !== "OK") ||
        (typeof serverMsg === "string" && /spam|form data is spam/i.test(serverMsg));

      if (looksSpam) {
        setStatus("error");
        setFormError(typeof serverMsg === "string" ? serverMsg : "提交失败，请稍后再试");
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请稍后重试。");
    }
  }

  return (
    <section className="bg-primary-500 py-14 text-background-50 md:py-20">
      <div className="mx-auto grid max-w-page gap-8 px-4 md:grid-cols-2 md:px-6 md:gap-12">
        <div className="flex flex-col justify-center">
          <span className="inline-block rounded-full bg-background-50/15 px-4 py-1.5 text-xs font-bold md:text-sm">
            联系我们
          </span>
          <h2 className="mt-4 font-heading text-2xl md:text-4xl">有疑问或建议？随时找我们聊聊</h2>
          <p className="mt-3 text-sm leading-relaxed text-background-100 md:text-base">
            无论是阅读上的小问题，还是关于平台功能的建议，我们都欢迎你的来信。
            工作日通常会在 24 小时内回复。
          </p>

          <div className="mt-6 space-y-3">
            <p className="flex items-center gap-3 text-sm md:text-base">
              <i className="ri-mail-line text-lg"></i>
              support@chongchong-read.example
            </p>
            <p className="flex items-center gap-3 text-sm md:text-base">
              <i className="ri-time-line text-lg"></i>
              周一至周五 9:00 – 18:00
            </p>
            <p className="flex items-center gap-3 text-sm md:text-base">
              <i className="ri-chat-smile-line text-lg"></i>
              遇到问题先看看上面的常见问题，也许已有答案
            </p>
          </div>
        </div>

        <form
          data-readdy-form="true"
          onSubmit={handleSubmit}
          className="rounded-2xl bg-background-50 p-6 text-foreground-900 md:p-8"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-xs font-bold text-foreground-800">你的称呼</span>
              <input
                type="text"
                name="name"
                required
                placeholder="怎么称呼你"
                className="w-full rounded-lg border border-background-200 bg-background-50 px-4 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-bold text-foreground-800">联系邮箱</span>
              <input
                type="email"
                name="email"
                required
                placeholder="you@example.com"
                className="w-full rounded-lg border border-background-200 bg-background-50 px-4 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
            </label>
          </div>

          <label className="mt-4 block">
            <span className="mb-1.5 block text-xs font-bold text-foreground-800">留言内容</span>
            <textarea
              name="message"
              required
              maxLength={500}
              rows={5}
              placeholder="说说你的问题或建议，最多 500 字"
              className="w-full resize-none rounded-lg border border-background-200 bg-background-50 px-4 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            ></textarea>
          </label>

          {/* Anti-spam honeypot — hidden via stylesheet */}
          <div className="form-trap-field" aria-hidden="true">
            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              readOnly
              aria-hidden="true"
            />
          </div>

          <button
            type="submit"
            disabled={status === "loading"}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
          >
            {status === "loading" ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                提交中…
              </>
            ) : (
              <>
                <i className="ri-send-plane-line"></i>
                发送留言
              </>
            )}
          </button>

          {status === "success" && (
            <p className="mt-4 flex items-center gap-2 rounded-lg bg-accent-100 px-4 py-3 text-sm font-medium text-accent-900">
              <i className="ri-checkbox-circle-line"></i>
              感谢你的留言！我们已经收到，会尽快与你联系。
            </p>
          )}
          {status === "error" && (
            <p className="mt-4 flex items-center gap-2 rounded-lg bg-primary-100 px-4 py-3 text-sm font-medium text-primary-800">
              <i className="ri-error-warning-line"></i>
              {formError || "提交失败，请稍后再试。"}
            </p>
          )}
        </form>
      </div>
    </section>
  );
}